import { useState, useEffect, useCallback } from "react";
import { buildApiUrl } from "../../utils/apiConfig";
import DeleteTaskModal from "../DeleteTaskModal";
import FileUploadModal from "../FileUploadModal";
import "../../styles/rag-admin.css";
import "../../styles/file-upload-modal.css";
import { apiClient, sendCredentialedXhr } from "../../utils/apiClient";

const API_BASE = buildApiUrl("api/rag");

interface CategoryOption {
    value: string;
    label: string;
}

const CATEGORIES: CategoryOption[] = [
    { value: "NCAA_POLICY", label: "NCAA Policy" },
    { value: "NIL_POLICY", label: "NIL Policy" },
    { value: "SPORT_RULES", label: "Sport Rules" },
    { value: "COLLEGE", label: "College / IPEDS" },
];

type IngestionStatus = "PROCESSING" | "READY" | "FAILED";

interface DocumentDto {
    id: number;
    title: string;
    category: string;
    fileName: string | null;
    chunkCount: number;
    ingestedAt: string;
    ingestionStatus?: IngestionStatus | string | null;
    ingestionError?: string | null;
}

function effectiveIngestionStatus(doc: DocumentDto): IngestionStatus {
    const status = doc.ingestionStatus?.toUpperCase();
    if (status === "PROCESSING" || status === "READY" || status === "FAILED") {
        return status;
    }
    return doc.chunkCount > 0 ? "READY" : "PROCESSING";
}

interface ToastState {
    message: string;
    type: "success" | "error";
}

async function fetchDocuments(): Promise<DocumentDto[]> {
    const res = await apiClient(`${API_BASE}/admin/documents`, { credentials: "include" });
    return res.ok ? ((await res.json()) as DocumentDto[]) : [];
}

async function uploadDocument(
    file: File,
    title: string,
    onProgress: (pct: number) => void,
): Promise<unknown> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);

    const xhr = await sendCredentialedXhr({
        method: "POST",
        url: `${API_BASE}/ingest`,
        body: formData,
        onUploadProgress: onProgress,
    });
    if (xhr.status >= 200 && xhr.status < 300) {
        try {
            return JSON.parse(xhr.responseText);
        } catch {
            return {};
        }
    }
    throw new Error(xhr.responseText || `HTTP ${xhr.status}`);
}

async function deleteDocumentRequest(id: number): Promise<void> {
    const res = await apiClient(`${API_BASE}/admin/documents/${id}`, {
        method: "DELETE",
        credentials: "include",
    });
    if (!res.ok) throw new Error("Delete failed");
}

export default function RagAdmin() {
    const [documents, setDocuments] = useState<DocumentDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>("ALL");
    const [search, setSearch] = useState("");
    const [showUpload, setShowUpload] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);

    const [title, setTitle] = useState("");
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState<DocumentDto | null>(null);
    const [deleting, setDeleting] = useState(false);

    const refreshDocs = useCallback(async (): Promise<DocumentDto[]> => {
        const docs = await fetchDocuments();
        setDocuments(docs);
        return docs;
    }, []);

    const loadDocs = useCallback(async (): Promise<void> => {
        setLoading(true);
        try {
            await refreshDocs();
        } finally {
            setLoading(false);
        }
    }, [refreshDocs]);

    const hasPendingIngestion = documents.some(
        (d) => effectiveIngestionStatus(d) === "PROCESSING",
    );

    useEffect(() => {
        void loadDocs();
    }, [loadDocs]);

    useEffect(() => {
        if (!hasPendingIngestion) return;
        const interval = setInterval(() => void refreshDocs(), 4000);
        return () => clearInterval(interval);
    }, [hasPendingIngestion, refreshDocs]);

    const showToast = (message: string, type: "success" | "error" = "success"): void => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const closeUploadModal = (): void => {
        if (uploading) return;
        setShowUpload(false);
        setTitle("");
    };

    const validateDocumentFile = (selectedFile: File): string | null => {
        const validTypes = [".pdf", ".txt", ".md"];
        const isValid = validTypes.some((t) => selectedFile.name.toLowerCase().endsWith(t));
        if (!isValid) {
            return "Only PDF, TXT, and MD files are supported";
        }
        return null;
    };

    const handleSubmit = async (file: File): Promise<void> => {
        if (!title.trim()) {
            showToast("Please enter a document title", "error");
            return;
        }
        const uploadedTitle = title.trim();
        setUploading(true);
        setProgress(0);
        try {
            await uploadDocument(file, uploadedTitle, setProgress);
            showToast(`Started ingesting "${uploadedTitle}". Processing in background...`);
            setShowUpload(false);
            setTitle("");
            setProgress(0);
            await refreshDocs();
            // Row is created synchronously on the server; poll until it appears or leaves PROCESSING.
            for (let attempt = 0; attempt < 30; attempt++) {
                const docs = await refreshDocs();
                const match = docs.find((d) => d.title === uploadedTitle);
                if (match && effectiveIngestionStatus(match) !== "PROCESSING") {
                    break;
                }
                if (match) {
                    break;
                }
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
        } catch (err) {
            showToast((err as Error).message || "Upload failed", "error");
        } finally {
            setUploading(false);
        }
    };

    const openDeleteModal = (doc: DocumentDto): void => {
        setDocToDelete(doc);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = (): void => {
        setDeleteModalOpen(false);
        setDocToDelete(null);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!docToDelete) return;
        setDeleting(true);
        try {
            await deleteDocumentRequest(docToDelete.id);
            showToast("Document deleted");
            setDocuments((prev) => prev.filter((d) => d.id !== docToDelete.id));
            closeDeleteModal();
        } catch {
            showToast("Failed to delete document", "error");
        } finally {
            setDeleting(false);
        }
    };

    const filteredDocs = documents
        .filter((d) => filter === "ALL" || d.category === filter)
        .filter((d) => !search || d.title.toLowerCase().includes(search.toLowerCase()));

    const stats = {
        total: documents.length,
        chunks: documents.reduce((sum, d) => sum + (d.chunkCount || 0), 0),
        pending: documents.filter((d) => effectiveIngestionStatus(d) === "PROCESSING").length,
        failed: documents.filter((d) => effectiveIngestionStatus(d) === "FAILED").length,
    };

    return (
        <div className="rag-admin">
            <div className="rag-admin-header">
                <div>
                    <h1>Knowledge Base</h1>
                    <p>Manage documents that power the AI assistant</p>
                </div>
                <button className="btn-primary" onClick={() => setShowUpload(true)}>
                    <PlusIcon /> Upload Document
                </button>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <span className="stat-label">Total Documents</span>
                    <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Total Chunks</span>
                    <span className="stat-value">{stats.chunks.toLocaleString()}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Processing</span>
                    <span className="stat-value">{stats.pending}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Failed</span>
                    <span className="stat-value">{stats.failed}</span>
                </div>
            </div>

            <div className="rag-filters">
                <div className="search-box">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Search documents..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="filter-tabs">
                    <button className={filter === "ALL" ? "active" : ""} onClick={() => setFilter("ALL")}>
                        All
                    </button>
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.value}
                            className={filter === c.value ? "active" : ""}
                            onClick={() => setFilter(c.value)}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="docs-table-wrapper">
                {loading ? (
                    <div className="empty-state">
                        <div className="spinner" />
                    </div>
                ) : filteredDocs.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📄</div>
                        <h3>No documents yet</h3>
                        <p>
                            Upload NCAA policies, NIL rules, or sport-specific documents to power the AI
                            assistant.
                        </p>
                        <button className="btn-primary" onClick={() => setShowUpload(true)}>
                            Upload Your First Document
                        </button>
                    </div>
                ) : (
                    <table className="docs-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Category</th>
                                <th>Chunks</th>
                                <th>Ingested</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocs.map((doc) => (
                                <tr key={doc.id}>
                                    <td>
                                        <div className="doc-title">{doc.title}</div>
                                        <div className="doc-filename">{doc.fileName}</div>
                                    </td>
                                    <td>
                                        <span className={`category-badge ${doc.category}`}>
                                            {CATEGORIES.find((c) => c.value === doc.category)?.label ||
                                                doc.category}
                                        </span>
                                    </td>
                                    <td>{doc.chunkCount || "—"}</td>
                                    <td>{new Date(doc.ingestedAt).toLocaleDateString()}</td>
                                    <td>
                                        {effectiveIngestionStatus(doc) === "PROCESSING" ? (
                                            <span className="status processing">
                                                <div className="status-dot" /> Processing
                                                {doc.chunkCount > 0 && (
                                                    <span className="status-detail">
                                                        {" "}
                                                        ({doc.chunkCount} chunks)
                                                    </span>
                                                )}
                                            </span>
                                        ) : effectiveIngestionStatus(doc) === "FAILED" ? (
                                            <div className="status-failed-cell">
                                                <span className="status failed">Failed</span>
                                                {doc.ingestionError && (
                                                    <div
                                                        className="ingestion-error"
                                                        title={doc.ingestionError}
                                                    >
                                                        {doc.ingestionError}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="status ready">
                                                <CheckIcon /> Ready
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            className="icon-btn-danger"
                                            onClick={() => openDeleteModal(doc)}
                                            disabled={deleting}
                                            title="Delete"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <FileUploadModal
                open={showUpload}
                onClose={closeUploadModal}
                title="Upload Document"
                accept=".pdf,.txt,.md"
                maxSizeBytes={25 * 1024 * 1024}
                hint="PDF, TXT, or MD · Max 25MB"
                previewMode="file"
                validateFile={validateDocumentFile}
                onFileSelect={(selectedFile) => {
                    if (!title) {
                        setTitle(selectedFile.name.replace(/\.[^.]+$/, ""));
                    }
                }}
                mode="custom"
                onSubmit={handleSubmit}
                submitLabel={uploading ? "Uploading..." : "Upload & Ingest"}
                submitDisabled={!title.trim()}
                busy={uploading}
                progress={progress}
                progressText={
                    progress < 100 ? `Uploading... ${progress}%` : "Processing document..."
                }
            >
                <div className="fum-form-group">
                    <label>Document Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. NCAA Division I Manual 2024"
                        disabled={uploading}
                    />
                </div>
            </FileUploadModal>

            <DeleteTaskModal
                open={deleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={() => void confirmDelete()}
                taskTitle={docToDelete?.title}
                itemLabel="Document"
            />

            {toast && <div className={`toast ${toast.type}`}>{toast.message}</div>}
        </div>
    );
}

const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const SearchIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
);
